import React from 'react';
import { User } from '../../../../../services/eventService';

interface AddUsersModalProps {
  show: boolean;
  onClose: () => void;
  unmatchedUsers: User[];
  selectedGroupForAddUsers: { roundNumber: number, groupId: string } | null;
  selectedUsersToAdd: string[];
  handleToggleUserSelection: (userId: string) => void;
  handleAddSelectedUsersToGroup: () => void;
}

const AddUsersModal: React.FC<AddUsersModalProps> = ({
  show,
  onClose,
  unmatchedUsers,
  selectedGroupForAddUsers,
  selectedUsersToAdd,
  handleToggleUserSelection,
  handleAddSelectedUsersToGroup
}) => {
  if (!show) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-person-plus me-2"></i>
              Add Users to Group
              {selectedGroupForAddUsers && (
                <span className="text-muted ms-2">
                  (Round {selectedGroupForAddUsers.roundNumber})
                </span>
              )}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            {unmatchedUsers.length > 0 ? (
              <>
                <p className="text-muted mb-3">
                  Select users to add to this group ({unmatchedUsers.length} available):
                </p>
                <div className="row g-3">
                  {unmatchedUsers.map((user) => {
                    const isSelected = selectedUsersToAdd.includes(user.User_ID);
                    return (
                      <div key={user.User_ID} className="col-12">
                        <div 
                          className={`card ${isSelected ? 'border-primary bg-light' : 'border-light'}`}
                          style={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            borderWidth: '2px'
                          }}
                          onClick={() => handleToggleUserSelection(user.User_ID)}
                        >
                          <div className="card-body py-3">
                            <div className="d-flex align-items-center">
                              {/* Selection indicator at the front */}
                              <div className="me-3 d-flex align-items-center justify-content-center" style={{ width: '24px', flexShrink: 0 }}>
                                {isSelected ? (
                                  <i className="bi bi-check-circle-fill text-primary fs-5"></i>
                                ) : (
                                  <div 
                                    className="rounded-circle border border-2 border-muted"
                                    style={{ width: '20px', height: '20px' }}
                                  ></div>
                                )}
                              </div>
                              
                              {/* User avatar */}
                              <div className="me-3" style={{ flexShrink: 0 }}>
                                {user.picture ? (
                                  <img 
                                    src={`data:image/jpeg;base64,${user.picture}`} 
                                    alt={`${user.firstname} ${user.lastname}`}
                                    className="rounded-circle"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                    onError={(e) => {
                                      // Fallback if image fails to load
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      const fallback = target.nextElementSibling as HTMLElement;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                                  style={{ 
                                    width: '50px', 
                                    height: '50px',
                                    display: user.picture ? 'none' : 'flex'
                                  }}
                                >
                                  <i className="bi bi-person-fill"></i>
                                </div>
                              </div>
                              
                              {/* User details */}
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <h6 className="mb-1 fw-bold">
                                      {user.firstname} {user.lastname}
                                    </h6>
                                    <div className="d-flex align-items-center gap-3">
                                      {user.firm && (
                                        <span className="badge bg-primary">
                                          {user.firm.name || user.firm.abbrev}
                                        </span>
                                      )}
                                      {user.jobtitle && (
                                        <span className="text-muted small">
                                          {user.jobtitle}
                                        </span>
                                      )}
                                      {user.mail && (
                                        <span className="text-muted small">
                                          <i className="bi bi-envelope me-1"></i>
                                          {user.mail}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {selectedUsersToAdd.length > 0 && (
                  <div className="alert alert-primary mt-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>{selectedUsersToAdd.length} user(s)</strong> selected for this group
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-people text-muted" style={{ fontSize: '3rem' }}></i>
                <h5 className="text-muted mt-3">No Available Users</h5>
                <p className="text-muted">All users for this round are already assigned to groups.</p>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleAddSelectedUsersToGroup}
              disabled={selectedUsersToAdd.length === 0}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Add {selectedUsersToAdd.length > 0 ? `${selectedUsersToAdd.length} ` : ''}User{selectedUsersToAdd.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUsersModal;
